import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, X, Camera, Shield, Calendar } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { PHOTO_GALLERY_CATEGORIES, PHOTO_GALLERY_SHOOTS } from "@/lib/photoGalleryData";
import { SITE, CALENDLY_URL } from "@/lib/siteData";
import { ROUTE_SEO } from "@/lib/routeSeo";

const ALL = "All";

const ProtectedImg = ({ src, alt, title, className, protect }) => {
  const handleContext = useCallback((e) => { if (protect) e.preventDefault(); }, [protect]);
  const handleDrag = useCallback((e) => { if (protect) e.preventDefault(); }, [protect]);
  return (
    <img
      src={src}
      alt={alt}
      title={title || alt}
      loading="lazy"
      decoding="async"
      onContextMenu={handleContext}
      onDragStart={handleDrag}
      className={className}
      style={protect ? { userSelect: "none", WebkitUserDrag: "none" } : undefined}
    />
  );
};

const Lightbox = ({ photos, index, onClose, onPrev, onNext, protect }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  if (index < 0 || !photos[index]) return null;
  const p = photos[index];
  return (
    <div
      data-testid="photo-lightbox"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="lightbox-prev"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="lightbox-next"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Next photo"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
      <motion.div
        key={p.id}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        className="relative max-w-7xl max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <ProtectedImg
          src={p.full}
          alt={p.alt || p.id}
          title={p.title}
          className="max-w-full max-h-[88vh] object-contain rounded-lg shadow-2xl"
          protect={protect}
        />
        <div className="mt-3 flex items-center justify-between text-xs text-white/70">
          <span className="uppercase tracking-[0.22em]">{index + 1} / {photos.length}</span>
          {protect && <span className="inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" />Protected · do not redistribute</span>}
        </div>
      </motion.div>
    </div>
  );
};

const ShootSection = ({ shoot, onOpen }) => {
  return (
    <section className="section-pad border-t border-border" data-testid={`shoot-${shoot.slug}`}>
      <div className="container-site">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="kicker mb-3">{shoot.category}</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(1.75rem,4vw,3rem)] leading-[0.95] tracking-[0.01em]">{shoot.label}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{shoot.count} photographs · click any image to view full size{shoot.protected ? " · client-owned, download disabled" : ""}.</p>
          </div>
          {shoot.protected && (
            <span className="inline-flex items-center gap-2 self-start rounded-full border border-[hsl(var(--primary))/0.4] bg-[hsl(var(--primary))/0.08] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
              <Shield className="h-3.5 w-3.5" />Protected portfolio
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          {shoot.photos.map((p, i) => (
            <button
              type="button"
              key={p.id}
              onClick={() => onOpen(shoot, i)}
              data-testid={`thumb-${shoot.slug}-${i}`}
              className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
            >
              <ProtectedImg
                src={p.thumb}
                alt={p.alt || `${shoot.label} - photo ${i + 1}`}
                title={p.title || shoot.label}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                protect={shoot.protected}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const EmptyState = () => (
  <section className="section-pad">
    <div className="container-site max-w-3xl">
      <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 sm:p-14 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-card text-[hsl(var(--primary))] mb-5">
          <Camera className="h-5 w-5" />
        </span>
        <h2 className="font-display uppercase text-foreground text-2xl sm:text-3xl tracking-[0.02em]">Photo portfolio launching soon</h2>
        <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
          We are finalising a brand new photographic showcase covering food & lifestyle TV, automotive,
          hospitality, commercial shoots, conference & event coverage, and healthcare. In the meantime,
          please email us for a curated portfolio relevant to your brief.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Button asChild className="h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-5">
            <a href={`mailto:${SITE.email}?subject=Photography%20portfolio%20enquiry`}>Email us for a portfolio <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
          <Button asChild variant="ghost" className="h-11 rounded-full border border-border bg-card/60 hover:bg-card px-5">
            <a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />Book a 15-min call</a>
          </Button>
        </div>
      </div>
    </div>
  </section>
);

export default function PhotoGallery() {
  const [activeCat, setActiveCat] = useState(ALL);
  const [lightbox, setLightbox] = useState({ open: false, shootIdx: 0, photoIdx: 0 });

  const cats = useMemo(() => [ALL, ...PHOTO_GALLERY_CATEGORIES], []);
  const shoots = useMemo(() => (
    activeCat === ALL ? PHOTO_GALLERY_SHOOTS : PHOTO_GALLERY_SHOOTS.filter((s) => s.category === activeCat)
  ), [activeCat]);

  const open = useCallback((shoot, photoIdx) => {
    const shootIdx = shoots.findIndex((s) => s.slug === shoot.slug);
    setLightbox({ open: true, shootIdx, photoIdx });
  }, [shoots]);
  const close = useCallback(() => setLightbox((lb) => ({ ...lb, open: false })), []);

  const currentShoot = shoots[lightbox.shootIdx];
  const next = useCallback(() => {
    if (!currentShoot) return;
    setLightbox((lb) => ({ ...lb, photoIdx: (lb.photoIdx + 1) % currentShoot.photos.length }));
  }, [currentShoot]);
  const prev = useCallback(() => {
    if (!currentShoot) return;
    setLightbox((lb) => ({ ...lb, photoIdx: (lb.photoIdx - 1 + currentShoot.photos.length) % currentShoot.photos.length }));
  }, [currentShoot]);

  const totalPhotos = PHOTO_GALLERY_SHOOTS.reduce((acc, s) => acc + (s.count || 0), 0);
  const subtitle = totalPhotos > 0
    ? `${totalPhotos.toLocaleString()} photographs across ${PHOTO_GALLERY_CATEGORIES.map(c => c.toLowerCase()).join(", ")} - click any image to view full size.`
    : "A curated showcase across food & lifestyle TV, automotive, hospitality, commercial, conference and healthcare shoots.";

  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/photography"].title}
        description={ROUTE_SEO["/photography"].meta}
        path="/photography"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Photography" }])]}
      />
      <PageHero
        kicker="Photography Portfolio"
        title={ROUTE_SEO["/photography"].h1}
        subtitle={subtitle}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Photography" }]}
      />

      {PHOTO_GALLERY_SHOOTS.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Category filters */}
          <section className="py-10 border-b border-border bg-[hsl(225,22%,4%)]">
            <div className="container-site">
              <div className="flex flex-wrap gap-2">
                {cats.map((c) => {
                  const active = activeCat === c;
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setActiveCat(c)}
                      data-testid={`cat-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`h-10 px-4 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                        active
                          ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent"
                          : "bg-card border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {shoots.map((s) => (<ShootSection key={s.slug} shoot={s} onOpen={open} />))}

          <AnimatePresence>
            {lightbox.open && currentShoot && (
              <Lightbox
                photos={currentShoot.photos}
                index={lightbox.photoIdx}
                onClose={close}
                onNext={next}
                onPrev={prev}
                protect={!!currentShoot.protected}
              />
            )}
          </AnimatePresence>
        </>
      )}

      <section className="section-pad">
        <div className="container-site">
          <div className="rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="kicker mb-3">Need stills for your next campaign?</p>
              <h2 className="font-display uppercase text-foreground text-[clamp(1.75rem,4.5vw,3.25rem)] leading-[0.95] tracking-[0.01em]">Hire us for a photographic shoot.</h2>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl">Brand photography, lifestyle, commercial, event, hospitality and food. Quoted bespoke per brief - book a 15-min call or email us a brief.</p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><Link to="/contact" data-testid="photography-contact-cta">Send a brief <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener"><Calendar className="mr-2 h-4 w-4" />Book a 15-min call</a></Button>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
